/**
 * IapManager — client-side in-app purchase lifecycle, modelled on Phone Dice's
 * 8-year-proven InAppManager. Uses cordova-plugin-purchase (CdvPurchase) on
 * native; a dev stub in the browser (no plugin) so the UI/flow is testable
 * without TestFlight.
 *
 * Server-authoritative delivery: the SERVER validates the receipt + credits the
 * wallet (see /api/store/purchase). The client only orders, forwards the receipt,
 * finish()es the transaction, and renders the granted contents.
 *
 * Lessons baked in (from the Phone Dice study):
 *  - finish() IMMEDIATELY after the server confirms, wrapped in try/catch (the
 *    iOS SDK throws spurious errors here); without it store.update() re-delivers
 *    the transaction on next launch → double-grant. Our server's IAPTransaction
 *    ledger dedupes too, so we're double-safe.
 *  - store.update() on init replays unfinished transactions → recovers a purchase
 *    that completed at the store but never got granted (app killed mid-flow).
 *  - guard flag prevents double-tap; serialized purchases keep pendingGrant single.
 */

import { Capacitor } from '@capacitor/core';
import { purchaseBundle, type IapGranted, type IapBalance } from '../api/iapApi';

/** All 11 SKUs (must match App Store Connect / Play Console + the server catalog). */
export const IAP_PRODUCT_IDS = [
  'sdl_pack_099', 'sdl_pack_299', 'sdl_pack_499', 'sdl_pack_999',
  'sdl_pack_1999', 'sdl_pack_4999', 'sdl_pack_9999',
  'sdl_offer_299', 'sdl_offer_499', 'sdl_offer_999', 'sdl_offer_2499',
] as const;

export interface PurchaseOpts {
  surface?: string;   // analytics surface (default 'store_browse')
  offerId?: string;   // for offer SKUs
  /** Dev-stub only: echo these dice into the mock grant so the offer dice-unlock
   *  reveal can be tested in the browser (no server credit). Ignored on device. */
  devDice?: string[];
}

export interface IapManagerDeps {
  /** JWT for the authenticated /api/store/purchase call. */
  getToken: () => string | null;
  /** Render the reward + refresh the wallet from server-delivered contents.
   *  `real` is false for the dev stub (no server credit) so the caller can avoid
   *  polluting real-balance state with mock values. */
  onGranted: (granted: IapGranted, balance: IapBalance, ctx: { productId: string; offerId?: string; real: boolean }) => void;
  /** Surface a failure (cancel / validation fail / network). */
  onError?: (productId: string, message: string) => void;
  /** Fired when the store loads / updates product info (localized prices ready). */
  onProductsUpdated?: () => void;
  /** Dev-stub only: current balance so the mock can fabricate a believable result. */
  getBalance?: () => IapBalance;
  /** Dev-stub only: the tapped bundle's amounts, so the mock reward reflects the
   *  actual item instead of fixed numbers. */
  devGrant?: (productId: string) => { stacks: number; gold: number; diceTokens: number } | null;
}

// Minimal view of the CdvPurchase global — the plugin ships no clean ESM types,
// and the native path is verified on-device. We strongly type our own data and
// keep this boundary loose.
type Cdv = any; // eslint-disable-line @typescript-eslint/no-explicit-any

export class IapManager {
  private deps: IapManagerDeps;
  private ready = false;
  private native = false;
  private purchasing: string | null = null;
  /** Context of the in-flight purchase (serialized, so a single slot is enough). */
  private current: { productId: string; surface?: string; offerId?: string; devDice?: string[] } | null = null;
  /** Server grant stashed in the validator, applied on `.verified`. */
  private pendingGrant: { granted: IapGranted; balance: IapBalance } | null = null;

  constructor(deps: IapManagerDeps) {
    this.deps = deps;
  }

  private cdv(): Cdv | null {
    return (window as unknown as { CdvPurchase?: Cdv }).CdvPurchase ?? null;
  }

  /**
   * Localized display price for a SKU (e.g. "€5.49", "¥800"), straight from the
   * store so the UI never hardcodes a currency. Returns null on web / dev-stub
   * or before the store has loaded the product, so callers can fall back.
   */
  getDisplayPrice(productId: string): string | null {
    if (!this.native) return null;
    const Cdv = this.cdv();
    const product = Cdv?.store?.get?.(productId);
    return product?.pricing?.price ?? null;
  }

  /** Register SKUs + initialize the store. Safe to call once after auth. */
  async init(): Promise<void> {
    if (this.ready) return;
    this.native = Capacitor.isNativePlatform();

    if (!this.native) {
      // Browser: no plugin. Dev stub handles purchases.
      this.ready = true;
      return;
    }

    const Cdv = this.cdv();
    if (!Cdv) {
      console.warn('[iap] native platform but CdvPurchase global missing — is cordova-plugin-purchase installed + synced?');
      return;
    }

    const store = Cdv.store;
    const platform = Capacitor.getPlatform() === 'ios'
      ? Cdv.Platform.APPLE_APPSTORE
      : Cdv.Platform.GOOGLE_PLAY;

    store.register(
      IAP_PRODUCT_IDS.map((id) => ({ id, type: Cdv.ProductType.CONSUMABLE, platform })),
    );

    // Receipt validator — CdvPurchase packages the platform receipt and calls
    // this; we forward to our server and translate the response. (Field names
    // verified on-device — they vary slightly by plugin version.)
    store.validator = (body: Cdv, callback: (res: Cdv) => void) => {
      void this.validate(body, callback);
    };

    store.when()
      .productUpdated(() => this.deps.onProductsUpdated?.())
      .approved((t: Cdv) => t.verify())
      .verified((receipt: Cdv) => {
        this.applyPendingGrant();
        this.safeFinish(receipt);
        this.clearPurchasing();
      })
      .unverified((receipt: Cdv) => {
        this.safeFinish(receipt);
        const pid = this.current?.productId ?? '';
        this.deps.onError?.(pid, 'validation_failed');
        this.clearPurchasing();
      });

    const iOS = Capacitor.getPlatform() === 'ios';
    store.initialize([{ platform, options: iOS ? { needAppReceipt: true } : {} }]);
    // Replay any unfinished transaction (interrupted-purchase recovery).
    store.update();
    this.ready = true;
  }

  /** Begin a purchase. Guards double-tap. */
  async purchase(productId: string, opts: PurchaseOpts = {}): Promise<void> {
    if (this.purchasing) return;
    if (!IAP_PRODUCT_IDS.includes(productId as typeof IAP_PRODUCT_IDS[number])) {
      this.deps.onError?.(productId, 'unknown_product');
      return;
    }
    this.purchasing = productId;
    this.current = { productId, surface: opts.surface, offerId: opts.offerId, devDice: opts.devDice };
    this.pendingGrant = null;

    if (!this.native) {
      await this.devStubPurchase(productId);
      return;
    }

    const Cdv = this.cdv();
    const platform = Capacitor.getPlatform() === 'ios'
      ? Cdv.Platform.APPLE_APPSTORE
      : Cdv.Platform.GOOGLE_PLAY;
    const product = Cdv?.store?.get(productId, platform);
    const offer = product?.getOffer?.();
    if (!offer) {
      this.deps.onError?.(productId, 'product_unavailable');
      this.clearPurchasing();
      return;
    }
    try {
      await offer.order();
    } catch {
      // Native order failed/cancelled — store.error also fires; clear here too.
      this.clearPurchasing();
    }
  }

  /** Validator: reshape CdvPurchase's body → our endpoint, POST, stash grant. */
  private async validate(body: Cdv, callback: (res: Cdv) => void): Promise<void> {
    const token = this.deps.getToken();
    if (!token) { callback({ ok: false, message: 'no_token' }); return; }

    const tx = body?.transaction ?? {};
    // The client unambiguously knows its own platform. Don't infer it from the
    // plugin's transaction.type string — that's 'ios-appstore' for StoreKit 1 but
    // 'apple-sk2' for StoreKit 2, and the latter fails an `includes('ios')` test
    // (would misroute every iOS purchase as Android).
    const platform: 'ios' | 'android' = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
    // Prefer the SKU we actually ordered (purchases are serialized, so `current`
    // is unambiguous). body.id is the fallback for the interrupted-transaction
    // replay at init, when there's no active purchase. (Apple's docs warn body.id
    // "can be set to your application identifier", so it's the fallback, not first.)
    const productId = this.current?.productId ?? body?.id ?? body?.products?.[0]?.id ?? '';
    const receipt = platform === 'ios' ? tx.appStoreReceipt : undefined;
    let purchaseToken: string | undefined;
    if (platform === 'android') {
      purchaseToken = tx.purchaseToken;
      if (!purchaseToken && typeof tx.receipt === 'string') {
        try { purchaseToken = JSON.parse(tx.receipt).purchaseToken; } catch { /* ignore */ }
      }
    }

    const res = await purchaseBundle(
      { productId, platform, receipt, purchaseToken, surface: this.current?.surface ?? 'store_browse', offerId: this.current?.offerId },
      token,
    );

    if (res.ok && res.granted) {
      this.pendingGrant = { granted: res.granted, balance: res.balance };
      callback({ ok: true });
    } else if (res.ok && res.duplicate) {
      // Already granted (replay) — no re-credit; just resolve cleanly.
      this.pendingGrant = null;
      callback({ ok: true });
    } else {
      callback({ ok: false, message: res.error ?? 'validation_failed' });
    }
  }

  private applyPendingGrant(): void {
    if (!this.pendingGrant || !this.current) return;
    const { granted, balance } = this.pendingGrant;
    this.deps.onGranted(granted, balance, { productId: this.current.productId, offerId: this.current.offerId, real: true });
    this.pendingGrant = null;
  }

  private safeFinish(receipt: Cdv): void {
    try { receipt?.finish?.(); } catch { /* iOS SDK can throw spuriously here */ }
  }

  private clearPurchasing(): void {
    this.purchasing = null;
    this.current = null;
  }

  // ── Dev stub (browser) ──────────────────────────────────────────────────────
  // Client-only mock: simulates a purchase and renders the reward with fabricated
  // contents. Does NOT credit the server (no real receipt) — for UI/flow testing.
  // REMOVE once native purchases are verified end-to-end on TestFlight.
  private async devStubPurchase(productId: string): Promise<void> {
    await new Promise((r) => setTimeout(r, 600)); // fake native sheet delay
    const cur = this.deps.getBalance?.() ?? { stacks: 0, gold: 0, diceTokens: 0 };
    const amt = this.deps.devGrant?.(productId);
    const granted: IapGranted = amt
      ? { stacks: amt.stacks, gold: amt.gold, diceTokens: amt.diceTokens }
      : { stacks: 100_000_000, gold: 500, diceTokens: 5 };
    // Echo the offer's dice so the unlock reveal can be tested in the browser.
    if (this.current?.devDice?.length) granted.dice = [...this.current.devDice];
    const balance: IapBalance = {
      stacks: cur.stacks + granted.stacks,
      gold: cur.gold + granted.gold,
      diceTokens: cur.diceTokens + granted.diceTokens,
    };
    this.deps.onGranted(granted, balance, { productId, offerId: this.current?.offerId, real: false });
    this.clearPurchasing();
  }
}
